import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import ShareTrackButton from '@/components/ShareTrackButton';
import {
  Play,
  Pause,
  ShoppingCart,
  ChevronLeft,
  Clock,
  Music2,
  Gauge,
  Disc3,
  FileAudio,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import CartDrawer from '@/components/CartDrawer';
import AudioPlayer from '@/components/AudioPlayer';
import FangateModal from '@/components/FangateModal';
import { ReleaseSEO } from '@/components/SEO';
import { toast } from 'sonner';
import { releaseLabel } from '@/constants/releaseTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const resolveCover = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

const MetaChip = ({ icon: Icon, label, value, testid }) => {
  if (!value && value !== 0) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-md border border-[#27272A] bg-[#0F0F11] px-3 py-1.5"
      data-testid={testid}
    >
      <Icon size={14} className="text-[#00F0FF]" />
      <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{label}</span>
      <span className="text-xs text-white font-medium">{value}</span>
    </div>
  );
};

const ReleaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart, cart, sessionId } = useCart();

  const [album, setAlbum] = useState(null);
  const [genres, setGenres] = useState([]);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);

  // Fangate state
  const [fangateOpen, setFangateOpen] = useState(false);
  const urlFangateId = searchParams.get('fg');
  const spotifyStatus = searchParams.get('spotify');

  const trackList = useMemo(() => {
    if (!album) return [];
    return album.tracks.map((t) => ({
      ...t,
      album_cover: resolveCover(album.cover_url),
      artist: album.artist,
    }));
  }, [album]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setAlbum(null);
    setCurrentTrack(null);
    (async () => {
      try {
        const [albumRes, genresRes, allRes] = await Promise.all([
          axios.get(`${API}/albums/${id}`),
          axios.get(`${API}/genres`),
          axios.get(`${API}/albums`),
        ]);
        if (cancelled) return;
        setAlbum(albumRes.data);
        // If the visitor arrived via /release/<UUID> or /release/<previous-slug>,
        // silently rewrite the URL bar to the canonical SEO slug so shares and
        // browser history use the pretty URL.
        try {
          const canonical = albumRes.data.slug;
          if (canonical && canonical !== id) {
            const qs = window.location.search || '';
            navigate(`/release/${canonical}${qs}`, { replace: true });
          }
        } catch { /* ignore navigation edge-cases */ }
        setGenres(genresRes.data);
        const albumGenres = albumRes.data.genre_ids || [];
        const rel = allRes.data
          .filter(
            (a) =>
              a.id !== albumRes.data.id &&
              (a.release_type === albumRes.data.release_type ||
                (a.genre_ids || []).some((g) => albumGenres.includes(g)))
          )
          .slice(0, 6);
        setRelated(rel);
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
          else console.error(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const inCart = album ? cart.some((c) => c.album_id === album.id) : false;
  const isFree = album?.release_type === 'free-download';

  // Auto-open fangate modal after Spotify OAuth redirect (?fg=...&spotify=ok|notfollowing|error)
  useEffect(() => {
    if (!album) return;
    if (urlFangateId && isFree) {
      setFangateOpen(true);
    }
  }, [album, urlFangateId, isFree]);

  const clearSpotifyQueryFlag = () => {
    // Keep fg id so the modal can retain state; drop the spotify flag after we've shown it
    if (spotifyStatus) {
      const next = new URLSearchParams(searchParams);
      next.delete('spotify');
      next.delete('reason');
      setSearchParams(next, { replace: true });
    }
  };

  const handleCloseFangate = () => {
    setFangateOpen(false);
    if (urlFangateId) {
      const next = new URLSearchParams(searchParams);
      next.delete('fg');
      next.delete('spotify');
      next.delete('reason');
      setSearchParams(next, { replace: true });
    }
  };

  const genreNames = useMemo(() => {
    if (!album) return [];
    return (album.genre_ids || [])
      .map((gid) => genres.find((g) => g.id === gid))
      .filter(Boolean);
  }, [album, genres]);

  const totalDuration = useMemo(() => {
    if (!album) return null;
    let total = 0;
    let valid = true;
    for (const t of album.tracks) {
      const parts = (t.duration || '').split(':').map(Number);
      if (parts.length !== 2 || parts.some(isNaN)) {
        valid = false;
        break;
      }
      total += parts[0] * 60 + parts[1];
    }
    if (!valid || total === 0) return null;
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, [album]);

  const playTrack = (i) => {
    setCurrentTrackIndex(i);
    setCurrentTrack(trackList[i]);
  };

  const handleAddToCart = () => {
    if (addToCart(album)) toast.success(`${album.title} added to cart`);
    else toast.info('Already in cart');
  };

  const handleNext = () => {
    if (currentTrackIndex < trackList.length - 1) {
      const i = currentTrackIndex + 1;
      setCurrentTrackIndex(i);
      setCurrentTrack(trackList[i]);
    }
  };
  const handlePrevious = () => {
    if (currentTrackIndex > 0) {
      const i = currentTrackIndex - 1;
      setCurrentTrackIndex(i);
      setCurrentTrack(trackList[i]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
        <SiteHeader onCartClick={() => setCartOpen(true)} />
        <main className="flex-1 max-w-screen-2xl mx-auto w-full px-6 lg:px-10 py-16">
          <div className="flex flex-col md:flex-row gap-10">
            <div className="w-full md:w-80 aspect-square rounded-lg bg-[#18181B] animate-pulse" />
            <div className="flex-1 space-y-4">
              <div className="h-4 w-24 bg-[#18181B] rounded animate-pulse" />
              <div className="h-10 w-2/3 bg-[#18181B] rounded animate-pulse" />
              <div className="h-4 w-40 bg-[#18181B] rounded animate-pulse" />
              <div className="h-24 w-full bg-[#18181B] rounded animate-pulse" />
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (notFound || !album) {
    return (
      <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
        <SiteHeader onCartClick={() => setCartOpen(true)} />
        <main className="flex-1 max-w-screen-2xl mx-auto w-full px-6 lg:px-10 py-24 text-center">
          <Disc3 className="mx-auto mb-4 text-[#27272A]" size={56} />
          <h1 className="text-2xl font-heading font-medium mb-2" data-testid="release-not-found">
            Release not found
          </h1>
          <p className="text-[#A1A1AA] mb-6 text-sm">
            This release may have been removed or the link is broken.
          </p>
          <Button
            onClick={() => navigate('/collections')}
            className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] rounded-full"
            data-testid="back-to-collections-btn"
          >
            Browse collections
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const cover = resolveCover(album.cover_url);

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
      <ReleaseSEO album={album} />
      <SiteHeader onCartClick={() => setCartOpen(true)} />

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-8 pb-40">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          data-testid="release-back-btn"
        >
          <ChevronLeft size={14} /> Back
        </button>

        {/* Hero */}
        <section
          className="relative rounded-2xl overflow-hidden border border-[#18181B] mb-12"
          data-testid="release-hero"
        >
          {/* Ambient background using cover */}
          <div
            className="absolute inset-0 opacity-40 blur-3xl scale-125"
            style={{
              backgroundImage: `url(${cover})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/50 via-[#09090B]/80 to-[#09090B]" />

          <div className="relative flex flex-col md:flex-row gap-8 lg:gap-12 p-6 md:p-10">
            <div className="w-full max-w-[280px] mx-auto md:mx-0 md:w-72 lg:w-80 shrink-0">
              <div className="aspect-square rounded-lg overflow-hidden shadow-2xl shadow-black/60 ring-1 ring-white/5">
                <img
                  src={cover}
                  alt={album.title}
                  className="w-full h-full object-cover"
                  data-testid="release-cover"
                />
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-between min-w-0">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span
                    className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] rounded-full bg-white/5 border border-white/10 text-white/80"
                    data-testid="release-type-badge"
                  >
                    {releaseLabel(album.release_type)}
                  </span>
                  {genreNames.map((g) => (
                    <Link
                      key={g.id}
                      to={`/collections?genre=${g.id}`}
                      className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition-colors"
                      data-testid={`release-genre-${g.slug}`}
                    >
                      {g.name}
                    </Link>
                  ))}
                </div>

                <h1
                  className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-2"
                  data-testid="release-title"
                >
                  {album.title}
                </h1>
                <p className="text-lg text-[#D4D4D8] mb-4" data-testid="release-artist">
                  {album.artist}
                </p>

                <div className="flex items-center gap-4 text-xs text-[#71717A] mb-6">
                  <span data-testid="release-track-count">
                    {album.tracks.length} {album.tracks.length === 1 ? 'track' : 'tracks'}
                  </span>
                  {totalDuration && (
                    <>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1" data-testid="release-total-duration">
                        <Clock size={12} /> {totalDuration}
                      </span>
                    </>
                  )}
                </div>

                {album.description && (
                  <p
                    className="text-sm text-[#A1A1AA] leading-relaxed max-w-2xl mb-6 whitespace-pre-line"
                    data-testid="release-description"
                  >
                    {album.description}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={() => playTrack(0)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#00F0FF] text-black font-medium hover:bg-[#33F3FF] transition-all hover:scale-[1.03]"
                  data-testid="release-play-all-btn"
                >
                  <Play size={16} fill="#000" stroke="#000" />
                  Play preview
                </button>

                {isFree ? (
                  <Button
                    onClick={() => setFangateOpen(true)}
                    className="rounded-full px-6 py-3 h-auto bg-white text-black hover:bg-[#E4E4E7]"
                    data-testid="release-free-download-btn"
                  >
                    <Download size={16} className="mr-2" />
                    Free download · Follow to unlock
                  </Button>
                ) : (
                  <Button
                    onClick={handleAddToCart}
                    disabled={inCart}
                    className={`rounded-full px-6 py-3 h-auto ${
                      inCart
                        ? 'bg-[#18181B] border border-[#27272A] text-[#71717A] cursor-not-allowed'
                        : 'bg-white text-black hover:bg-[#E4E4E7]'
                    }`}
                    data-testid="release-add-to-cart-btn"
                  >
                    <ShoppingCart size={16} className="mr-2" />
                    {inCart ? 'In cart' : `Add to cart · ₱${album.price.toFixed(2)}`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Tracklist */}
        <section className="mb-16" data-testid="release-tracklist-section">
          <div className="flex items-end justify-between gap-4 mb-4">
            <h2 className="text-xl md:text-2xl font-heading font-medium tracking-tight">Tracklist</h2>
            <span className="text-xs text-[#71717A]">Click a row to preview</span>
          </div>

          <div className="rounded-lg border border-[#18181B] overflow-hidden">
            {/* Header row (desktop) */}
            <div className="hidden md:grid grid-cols-[40px_1fr_90px_80px_100px_100px_60px] gap-4 px-4 py-3 border-b border-[#18181B] text-[10px] uppercase tracking-[0.2em] text-[#52525B] bg-[#0B0B0D]">
              <div>#</div>
              <div>Title</div>
              <div className="text-center">BPM</div>
              <div className="text-center">Key</div>
              <div>Version</div>
              <div>File</div>
              <div className="text-right">
                <Clock size={12} className="inline" />
              </div>
            </div>

            {album.tracks.map((t, i) => {
              const isActive = currentTrack?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => playTrack(i)}
                  className={`w-full text-left grid grid-cols-[40px_1fr_60px] md:grid-cols-[40px_1fr_90px_80px_100px_100px_60px] gap-4 px-4 py-3 border-b border-[#18181B] last:border-b-0 items-center transition-colors ${
                    isActive ? 'bg-[#00F0FF]/5' : 'hover:bg-[#0F0F11]'
                  }`}
                  data-testid={`track-row-${i}`}
                >
                  <div className="text-sm text-[#71717A] w-8 flex items-center justify-center">
                    {isActive ? (
                      <Pause size={14} className="text-[#00F0FF]" />
                    ) : (
                      <span className="group-hover:hidden">{i + 1}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div
                      className={`font-medium truncate ${isActive ? 'text-[#00F0FF]' : 'text-white'}`}
                      data-testid={`track-title-${i}`}
                    >
                      {t.title}
                    </div>
                    {/* Mobile inline meta */}
                    <div className="md:hidden flex items-center gap-2 mt-1 flex-wrap text-[10px] text-[#71717A]">
                      {t.bpm ? <span>{t.bpm} BPM</span> : null}
                      {t.key ? <span>· {t.key}</span> : null}
                      {t.version ? <span>· {t.version}</span> : null}
                      {t.file_type ? <span>· {t.file_type}</span> : null}
                    </div>
                  </div>
                  <div className="hidden md:block text-center text-sm text-[#D4D4D8]" data-testid={`track-bpm-${i}`}>
                    {t.bpm || '—'}
                  </div>
                  <div className="hidden md:block text-center text-sm text-[#D4D4D8]" data-testid={`track-key-${i}`}>
                    {t.key || '—'}
                  </div>
                  <div className="hidden md:block text-sm text-[#D4D4D8] truncate" data-testid={`track-version-${i}`}>
                    {t.version || '—'}
                  </div>
                  <div className="hidden md:block text-sm text-[#D4D4D8] truncate uppercase" data-testid={`track-filetype-${i}`}>
                    {t.file_type || '—'}
                  </div>
                  <div className="text-right text-sm text-[#71717A] flex items-center justify-end gap-2" data-testid={`track-duration-${i}`}>
                    <span>{t.duration}</span>
                    <span onClick={(e) => e.stopPropagation()}>
                      <ShareTrackButton
                        title={t.title}
                        artist={album.artist}
                        bpm={t.bpm}
                        key={t.id}
                        musicalKey={t.key}
                        releaseType={album.release_type}
                        shareUrl={`${window.location.origin}/release/${album.id}#t${i}`}
                        compact
                        testId={`share-track-${i}`}
                      />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Meta chips summary */}
          <div className="mt-6 flex flex-wrap gap-2">
            <MetaChip icon={Music2} label="Tracks" value={album.tracks.length} testid="chip-tracks" />
            {totalDuration && (
              <MetaChip icon={Clock} label="Total" value={totalDuration} testid="chip-total" />
            )}
            <MetaChip icon={Disc3} label="Type" value={releaseLabel(album.release_type)} testid="chip-type" />
            {album.tracks.some((t) => t.bpm) && (
              <MetaChip
                icon={Gauge}
                label="BPM range"
                value={(() => {
                  const bpms = album.tracks.map((t) => t.bpm).filter(Boolean);
                  const min = Math.min(...bpms);
                  const max = Math.max(...bpms);
                  return min === max ? `${min}` : `${min}–${max}`;
                })()}
                testid="chip-bpm-range"
              />
            )}
            {album.tracks.some((t) => t.file_type) && (
              <MetaChip
                icon={FileAudio}
                label="Formats"
                value={[...new Set(album.tracks.map((t) => t.file_type).filter(Boolean))]
                  .map((f) => f.toUpperCase())
                  .join(', ')}
                testid="chip-formats"
              />
            )}
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section data-testid="related-releases-section">
            <div className="flex items-end justify-between gap-4 mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-medium tracking-tight">
                You might also like
              </h2>
              <Link
                to="/collections"
                className="text-xs text-[#A1A1AA] hover:text-white underline underline-offset-2"
                data-testid="see-all-releases-link"
              >
                See all
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {related.map((r) => (
                <Link
                  to={`/release/${r.id}`}
                  key={r.id}
                  className="group"
                  data-testid={`related-card-${r.id}`}
                >
                  <div className="relative mb-3 rounded-lg overflow-hidden bg-[#121214] transition-transform duration-300 group-hover:scale-[1.02]">
                    <img
                      src={resolveCover(r.cover_url)}
                      alt={r.title}
                      className="w-full aspect-square object-cover"
                    />
                    {r.release_type && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white uppercase tracking-wider">
                        {releaseLabel(r.release_type)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading font-medium truncate">{r.title}</h3>
                  <p className="text-sm text-[#A1A1AA] truncate">{r.artist}</p>
                  <p className="text-[#00F0FF] text-sm font-medium mt-1">
                    ₱{r.price.toFixed(2)}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <AudioPlayer
        currentTrack={currentTrack}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onClose={() => setCurrentTrack(null)}
      />
      {isFree && (
        <FangateModal
          open={fangateOpen}
          album={album}
          fangateId={urlFangateId}
          sessionId={sessionId}
          spotifyBanner={spotifyStatus}
          spotifyReason={searchParams.get('reason') || ''}
          onClose={handleCloseFangate}
          onUnlocked={clearSpotifyQueryFlag}
        />
      )}
    </div>
  );
};

export default ReleaseDetailPage;
